import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import paths from 'routes/paths';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Function to generate a GUID (Token)
const generateToken = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
};

const ForgotPasswordForm = () => {
    // State to store user data
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null); // Initially null

    // Email check API
    const checkEmailExists = async (email) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/userRoute/checkEmail/${email}`, {
                headers: {
                    Authorization: process.env.REACT_APP_BasicAuthSecret
                }
            }
            );
            if (response.data.exists) {
                setUserData(response.data.userData);
            } else {
                setUserData(null); // No user found
            }
            return response.data.exists;
        } catch (error) {
            console.error("Error checking email existence:", error);
            setUserData(null); // Set to null in case of an error
            return false;
        }
    };

    const fetchUserByEmail = async (email) => {
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/userRoute/checkEmail/${email}`,
            {
                headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
            }
        );

        if (!response.data.exists) {
            throw new Error("User not found");
        }

        return response.data.userData;
    };

    const validateEmailExists = async (value) => {
        if (!value) return true; // Return true if no value to bypass validation
        const exists = await checkEmailExists(value);
        return exists; // Return true if email exists
    };

    // Validation Schema
    const schema = yup.object({
        email: yup
            .string()
            .email(t('invalidEmailText'))
            .required(t('emailAddressRequiredText'))
            .test('email-exists', t('emailNotFoundText'), async (value) => {
                return validateEmailExists(value);
            }),
    }).required();

    const methods = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
    });

    const { handleSubmit, register, control, formState: { errors }, reset, setValue, trigger } = methods;
    const [loading, setIsLoading] = useState(false);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const fieldsWithErrors = Object.keys(errors);
            trigger(fieldsWithErrors);
        }
    }, [i18n.language]);

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            //  ALWAYS fetch fresh user data here
            const user = await fetchUserByEmail(data.email);

            const culture = localStorage.getItem('culture') || 'en';
            const token = generateToken();
            const resetLink = `${window.location.origin}${paths.resetPassword}?token=${token}&culture=${culture}`;

            // Send reset email
            await axios.post(
                `${process.env.REACT_APP_API_URL}/emailRoute/sendResetPasswordEmail`,
                {
                    email: data.email,
                    name: user.Name,
                    resetLink,
                    culture,
                    customerId: user.CustomerId
                },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            //  Save token
            await axios.post(
                `${process.env.REACT_APP_API_URL}/tokenDataRoute/createToken`,
                {
                    token,
                    userId: user.UserId,
                    tokenUsed: false
                },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            toast.success(t('resetLinkSuccess'), {
                position: "bottom-left",
                autoClose: 5000
            });

            navigate('/authentication/login');

        } catch (error) {
            console.error("Forgot password failed:", error);
            toast.error(t('errorOccuredRetryText'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Form className="mt-4" onSubmit={handleSubmit(onSubmit)}>
            <Form.Group className="mb-3">
                <Form.Control
                    type="email"
                    {...register('email')}
                    isInvalid={!!errors.email}
                    placeholder={t('emailAddress')}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.email?.message}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Button className="w-100" type="submit" disabled={loading}>
                    {loading ? t('loading') : t('sendResetLink')}
                </Button>
            </Form.Group>
        </Form>
    );
};

export default ForgotPasswordForm;
